<template>
  <Loader
    :value="promises.fetchOne"
    :message="`${this.$t('loading', { resource: labels.residentialAccount.toLowerCase() })}...`"
    class="residential-account"
  >
    <div>
      <Unit v-if="reference" data-widget="unit">
        <template #actions>
          <div class="residential-account__actions">
            <ButtonComponent
              :label="complaintRequestLabel"
              theme="secondary"
              :icon="['fal', 'comment']"
              @click="trigger('contact-seller')"
            />
            <ButtonComponent
              v-if="hasComplaintAddon && !$path('metadata.settings.complaint.blockReceiving', item)"
              :label="$tc('complaint', 1)"
              theme="secondary"
              :icon="['fal', 'plus']"
              @click="trigger('complaint')"
            />
          </div>
        </template>
      </Unit>
      <div class="residential-account__content">
        <FDV />

        <Shop v-if="reference && reference.shop && $path('metadata.settings.showShop', item)" :value="reference" />

        <TaskTable
          v-if="hasComplaintAddon"
          :default-filter="{
            search: '',
            type: 'complaint',
            status: ['undone', 'request'],
            reference: `ResidentialAccount:${id}`,
          }"
          :enabled-columns="['title', 'category', 'status', 'members']"
          data-widget="tasks"
          :title="$tc('complaint', 2)"
        >
          <template #actions>
            <ButtonComponent
              v-if="!$path('metadata.settings.complaint.blockReceiving', item)"
              @click="trigger('complaint')"
              :label="$t('create')"
              :icon="['fal', 'plus']"
              theme="secondary"
            />
          </template>
        </TaskTable>
      </div>

      <Contact />
      <Complaint />
    </div>
  </Loader>
</template>

<script>
import Bus from '@/bus'
import { mapi18n } from '@/i18n'
import { mapActions, mapState } from 'vuex'

import TaskTable from '@/components/Task/Table/Component'
import Widgets from './Widgets'

export default {
  props: {
    id: String,
  },
  watch: {
    id: {
      handler: 'fetch',
      immediate: true,
    },
  },
  data() {
    return {
      links: [],
    }
  },
  metaInfo() {
    return {
      title: this.reference
        ? `${this.labels.residentialAccount} - ${this.$path('reference.name')} `
        : this.labels.loading,
    }
  },
  computed: {
    ...mapState('ResidentialAccount', ['promises', 'item']),
    ...mapi18n('labels', {
      loading() {
        return this.$tc('loading') + '...'
      },
      residentialAccount: 'residentialAccount',
    }),
    complaintRequestLabel() {
      return this.$tc('new') + ' ' + this.$tc('complaintRequest', 1).toLowerCase()
    },
    reference() {
      return this.$path('reference', this.item)
    },
    shop() {
      return this.$path('shop', this.item)
    },
    hasComplaintAddon() {
      return (this.$path('reference.project.metadata.addons') || []).includes('project:complaint')
    },
  },
  methods: {
    ...mapActions('ResidentialAccount', {
      fetch(dispatch) {
        return dispatch('fetch', { id: this.id })
          .then(() => {
            if (!this.item.id) return this.$router.replace({ name: 'notfound' })
          })
          .catch(() => this.$router.replace({ name: 'residential-account-login' }))
      },
    }),
    trigger(key) {
      Bus.$emit(key + ':show')
    },
  },

  components: {
    TaskTable,
    ...Widgets,
  },
}
</script>

<style lang="scss">
.residential-account {
  --residential-account-spacing: 2rem;
  --residential-account-max-width: 1350px;
  --residential-account-transform-y: -10rem;
  --residential-account-responsive-transform: 4rem;

  padding-bottom: 3rem;
  background-color: $background-grey;

  @include respond-below('phone') {
    background-color: #eaeaea;
    --residential-account-spacing: 1.5rem;
  }

  [data-widget='unit'] {
    .residential-account-unit__content {
      transform: translate(0, var(--residential-account-transform-y));
      margin: 0 auto;
      max-width: var(--residential-account-max-width);

      @include respond-below('phone') {
        transform: translate(
          0,
          calc(var(--residential-account-transform-y) + var(--residential-account-responsive-transform))
        );
      }
    }
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    padding-bottom: 0.5rem;

    @include respond-below('phone') {
      flex-direction: column;
    }

    .elder-button {
      font-size: 0.9em;
    }
  }

  &__content {
    transform: translate(0, calc(var(--residential-account-transform-y) + var(--residential-account-spacing)));
    max-width: var(--residential-account-max-width);
    margin: 0 auto;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
    gap: var(--residential-account-spacing);

    @include respond-below('phone') {
      grid-template-columns: 1fr;
      transform: translate(
        0,
        calc(
          var(--residential-account-transform-y) + var(--residential-account-spacing) +
            var(--residential-account-responsive-transform)
        )
      );

      .card {
        box-shadow: none;
      }
    }

    [data-widget='tasks'] {
      grid-column-end: span 2;

      @include respond-below('tablet') {
        grid-column-end: span 1;
      }

      h2 {
        margin: 0;
      }

      .card {
        height: 100%;
      }
    }
  }
}

.route--residential-account {
  .comment-input {
    border: 1px solid var(--border-color);
  }
}
</style>
